import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { PageControl } from '../model/page-control';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import {
	CardUrlRequest,
	CardUrlResponse,
	Cardholder,
	CardholderUpdateRequest,
	CardholderPageableResponse,
	SimplifiedCardholderHelper
} from '@app-model/cardholder';
import { ClientPageableResponse, FileUploader } from '@app-model/client';
import { DefaultStatus } from '@app-model/login';
import { PromotedContent } from '@app-model/promotedContent';

@Injectable({
	providedIn: 'root'
})
export class CardholderService {
	constructor(private http: HttpClient, private toastr: ToastrService) {}

	get(id: string): Observable<Cardholder> {
		return this.http.get<Cardholder>(`${environment.api}/cardholder/${id}`);
	}

	getSimplifiedCardholder(id: string): Observable<SimplifiedCardholderHelper> {
		return this.http.get<SimplifiedCardholderHelper>(`${environment.api}/cardholder/simplified/${id}`);
	}

	getPromotedContents(cardholderId: string): Observable<PromotedContent[]> {
		return this.http.get<PromotedContent[]>(`${environment.api}/cardholder/promotedContents/${cardholderId}`);
	}

	savePromotedContents(cardholderId: string, promotedContents: PromotedContent[]): Observable<PromotedContent[]> {
		return this.http.post<PromotedContent[]>(
			`${environment.api}/cardholder/savePromotedContents/${cardholderId}`,
			promotedContents
		);
	}

	paginate(
		pageControl: PageControl,
		clientId?: string,
		groupId?: string,
		searchTerm?: string
	): Observable<CardholderPageableResponse> {
		const body: any = {
			page: pageControl.page,
			pageSize: pageControl.limit,
			sortDirection: pageControl.order,
			sortField: pageControl.orderField
		};

		if (pageControl.param && pageControl.param !== '' && pageControl.value && pageControl.value !== '') {
			body.param = pageControl.param;
			body.value = pageControl.value;
		}
		const params = {};
		if (clientId) {
			params['clientId'] = clientId;
		}
		if (groupId) {
			params['groupId'] = groupId;
		}
		if (searchTerm) {
			params['query'] = searchTerm;
		}

		return this.http.post<CardholderPageableResponse>(`${environment.api}/cardholder/list`, body, { params });
	}

	paginateClient(pageControl: PageControl, clientId?: string, groupId?: string): Observable<ClientPageableResponse> {
		const body: any = {
			page: pageControl.page,
			pageSize: pageControl.limit,
			sortDirection: pageControl.order,
			sortField: pageControl.orderField
		};

		if (pageControl.param && pageControl.param !== '' && pageControl.value && pageControl.value !== '') {
			body.param = pageControl.param;
			body.value = pageControl.value;
		}
		const params = {};
		if (clientId) {
			params['clientId'] = clientId;
		}
		if (groupId) {
			params['groupId'] = groupId;
		}

		return this.http.post<ClientPageableResponse>(`${environment.api}/client/list`, body, { params });
	}

	getCardUrl(cardUrlRequest: CardUrlRequest): Observable<CardUrlResponse> {
		return this.http.post<CardUrlResponse>(`${environment.api}/cardholder/cardUrl`, cardUrlRequest);
	}

	uploadImage(file: File): Observable<FileUploader> {
		const body = new FormData();
		body.append('file', file);
		return this.http.post<FileUploader>(`${environment.api}/cardholder/uploadPicture`, body);
	}

	uploadImageUpdate(file: File, id: string): Observable<FileUploader> {
		const body = new FormData();
		body.append('file', file);
		return this.http.post<FileUploader>(`${environment.api}/cardholder/uploadPicture/${id}`, body);
	}

	saveCardholder(body: CardholderUpdateRequest): Observable<any> {
		return this.http.post(`${environment.api}/cardholder`, body);
	}

	remove(id: string): Observable<DefaultStatus> {
		return this.http.post<DefaultStatus>(`${environment.api}/cardholder/remove/${id}`, {});
	}

	syncConfig(cardholderId: string, configId: string): Observable<DefaultStatus> {
		return this.http.post<DefaultStatus>(
			`${environment.api}/cardholder/runIntegrationSync/${cardholderId}/${configId}`,
			{}
		);
	}

	uploadBannerImage(file: File, cardholderId: string): Observable<FileUploader> {
		const body = new FormData();
		body.append('file', file);
		return this.http.post<FileUploader>(`${environment.api}/cardholder/uploadPromotionBanner/${cardholderId}`, body);
	}

	uploadImageGridImage(file: File, cardholderId: string): Observable<FileUploader> {
		const body = new FormData();
		body.append('file', file);
		return this.http.post<FileUploader>(`${environment.api}/cardholder/uploadPromotionGrid/${cardholderId}`, body);
	}

	uploadCarrouselImage(file: File, cardholderId: string, fileType: string): Observable<FileUploader> {
		const body = new FormData();
		body.append('file', file);
		return this.http.post<FileUploader>(
			`${environment.api}/cardholder/uploadPromotionCarrousel/${cardholderId}/${fileType}`,
			body
		);
	}

	uploadPdf(file: File, cardholderId: string): Observable<FileUploader> {
		const body = new FormData();
		body.append('file', file);
		return this.http.post<FileUploader>(`${environment.api}/cardholder/uploadPdf/${cardholderId}`, body);
	}
}
